import { Button } from '@televet/kibble-ui/Button';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { Card } from '@televet/kibble-ui/Card';
import { Menu } from '@televet/kibble-ui/Menu';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DeleteDialog } from '@/components/DeleteDialog';
import { PageContainer } from '@/components/PageContainer';
import { toaster } from '@/components/ui/toaster';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/_authenticated/agents/')({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      pageTitle: 'Agents',
    };
  },
});

function RouteComponent() {
  const { data: agentsData } = useQuery(trpc.agent.list.queryOptions({}));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [agentToDelete, setAgentToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [agentToMakeDefault, setAgentToMakeDefault] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const currentDefaultName = agentsData?.agents.find(
    (a) => a.isDefaultAgent
  )?.name;

  const { mutate: deleteAgent, isPending: isDeleting } = useMutation(
    trpc.agent.deleteOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Agent deleted',
          description: data.message,
        });
        setAgentToDelete(null);
        queryClient.invalidateQueries({ queryKey: trpc.agent.pathKey() });
      },
      onError: (error: any) => {
        toaster.error({
          title: 'Failed to delete agent',
          description: error.message || 'An error occurred',
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const { mutate: makeDefaultAgent, isPending: isMakingDefault } = useMutation(
    trpc.agent.updateOne.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Default agent updated' });
        setAgentToMakeDefault(null);
        queryClient.invalidateQueries({ queryKey: trpc.agent.pathKey() });
      },
      onError: (error: any) => {
        toaster.error({
          title: 'Failed to update default agent',
          description: error.message || 'An error occurred',
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Flex justify="flex-end">
          <Button onClick={() => navigate({ to: '/agents/add' })}>
            Add Agent
          </Button>
        </Flex>
        <Flex flex="1 1 auto" justify="center" align="flex-start">
          <Card
            flex="1 1 auto"
            direction="column"
            borderColor="border.default"
            rounded="lg"
            overflow="hidden"
          >
            <Table.Root size="md" interactive>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Name</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end" width="100px">
                    Actions
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {agentsData?.agents.map((agent) => (
                  <Table.Row key={agent.id}>
                    <Table.Cell
                      cursor="pointer"
                      onClick={() => navigate({ to: `/agents/${agent.id}` })}
                    >
                      <Flex align="center" gap={2}>
                        {agent.name}
                        <Tag label={agent.type} variant="default" />
                        {agent.isDefaultAgent && (
                          <Tag label="Default" variant="info" />
                        )}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell
                      textAlign="end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Menu
                        buttonProps={{
                          iconName: 'dotsVertical',
                          variant: 'ghostNeutral',
                          size: 'sm',
                        }}
                        listProps={{
                          menuItems: [
                            {
                              type: 'default',
                              label: 'Make Default',
                              value: 'make-default',
                              // Prompt and WorkflowTask agents cannot drive chat
                              // and are rejected as default server-side.
                              disabled:
                                agent.isDefaultAgent ||
                                agent.type === 'Prompt' ||
                                agent.type === 'WorkflowTask',
                            },
                            {
                              type: 'default',
                              label: 'Delete',
                              value: 'delete',
                              disabled: agent.isDefaultAgent,
                              color: agent.isDefaultAgent
                                ? undefined
                                : 'red.500',
                            },
                          ],
                          onSelect: ({ item }) => {
                            if (
                              item.value === 'make-default' &&
                              agent.type !== 'Prompt' &&
                              agent.type !== 'WorkflowTask'
                            ) {
                              setAgentToMakeDefault({
                                id: agent.id,
                                name: agent.name,
                              });
                            } else if (
                              item.value === 'delete' &&
                              !agent.isDefaultAgent
                            ) {
                              setAgentToDelete({
                                id: agent.id,
                                name: agent.name,
                              });
                            }
                          },
                        }}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Card>
        </Flex>
      </Flex>

      <ConfirmDialog
        isOpen={!!agentToMakeDefault}
        title="Change Default Agent"
        description={
          currentDefaultName
            ? `"${currentDefaultName}" is currently the default agent. Setting "${agentToMakeDefault?.name}" as default will replace it for all conversations that don't specify an agent.`
            : `Set "${agentToMakeDefault?.name}" as the default agent for all conversations that don't specify an agent.`
        }
        confirmLabel="Set as Default"
        onConfirm={() => {
          if (agentToMakeDefault) {
            makeDefaultAgent({
              id: agentToMakeDefault.id,
              data: { isDefaultAgent: true },
            });
          }
        }}
        onCancel={() => setAgentToMakeDefault(null)}
        isLoading={isMakingDefault}
      />

      <DeleteDialog
        isOpen={!!agentToDelete}
        title="Delete Agent"
        description={`Are you sure you want to delete "${agentToDelete?.name}"? This action cannot be undone.`}
        onConfirm={() => {
          if (agentToDelete) {
            deleteAgent({ id: agentToDelete.id });
          }
        }}
        onCancel={() => setAgentToDelete(null)}
        isLoading={isDeleting}
        isDangerous
      />
    </PageContainer>
  );
}
